import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconWaveSine } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import {
  getSerial,
  getSerialHistory,
  type SerialCustomerDto,
  type SerialMovementDto,
} from './api';

const HISTORY_PAGE = 200;

/**
 * One unit: where it is, who has had it, and everything that ever happened to it.
 *
 * **Shorter than the lot trace, and deliberately.** A lot needs received / shipped / adjusted /
 * on-hand totals because it is a quantity split across places and customers. A serial is one
 * thing: it is either here or it is with somebody, and the four figures would all be 1 or 0.
 *
 * **Every customer row is certain.** The lot trace has to mark some rows "may have received",
 * because a wave's ledger rows name the wave and a lot is split across the orders on it. A
 * serial is one unit going to one order and 086 records it on both paths, so there is no such
 * qualifier here and none is missing.
 *
 * **Warehouse-scoped**, like everything else that can name a customer.
 */
export function SerialDetailRoute() {
  const { t } = useTranslation();
  const router = useRouter();
  const { id } = useParams({ strict: false }) as { id: string };
  const warehouseId = useCurrentWarehouseId();
  const [page, setPage] = useState(1);

  const report = useQuery({
    queryKey: ['serial', id, warehouseId] as const,
    queryFn: ({ signal }) => getSerial(id, signal),
    enabled: Boolean(warehouseId && id),
  });

  const history = useQuery({
    queryKey: ['serial', id, warehouseId, 'history', page] as const,
    queryFn: ({ signal }) => getSerialHistory(id, { pageNumber: page }, signal),
    enabled: Boolean(warehouseId && id),
  });

  const customerColumns: DataColumn<SerialCustomerDto>[] = useMemo(() => [
    {
      key: 'customer',
      label: t('serialDetail.customer'),
      card: 'title',
      render: (c) => (
        <Stack gap={0}>
          <Group gap="xs">
            <Text size="sm" fw={500}>{c.customerName}</Text>
            {/* Not a caveat — a fact about how it left. A unit that went out on a wave is as
                certainly theirs as one that went out on its own pick. */}
            {c.fromWave && (
              <Badge size="sm" color="blue" variant="light"
                leftSection={<IconWaveSine size={12} />}>
                {t('serialDetail.wave')}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">{c.customerNumber}</Text>
        </Stack>
      ),
    },
    {
      key: 'order',
      label: t('serialDetail.order'),
      card: 'subtitle',
      render: (c) => (
        <Stack gap={0}>
          <Code>{c.orderNumber}</Code>
          {c.customerPo ? <Text size="xs" c="dimmed">{c.customerPo}</Text> : null}
        </Stack>
      ),
    },
    {
      key: 'shipped',
      label: t('serialDetail.shipped'),
      card: 'aside',
      render: (c) => (c.shippedAtUtc
        ? <Text size="sm">{new Date(c.shippedAtUtc).toLocaleDateString()}</Text>
        : <Text size="sm" c="dimmed">—</Text>),
    },
    {
      key: 'pick',
      label: t('serialDetail.doc'),
      render: (c) => (c.pickNumber ? <Code>{c.pickNumber}</Code> : <Text size="sm" c="dimmed">—</Text>),
    },
  ], [t]);

  const movementColumns: DataColumn<SerialMovementDto>[] = useMemo(() => [
    {
      key: 'when',
      label: t('serialDetail.when'),
      card: 'subtitle',
      render: (m) => <Text size="sm">{new Date(m.performedAtUtc).toLocaleString()}</Text>,
    },
    {
      key: 'what',
      label: t('serialDetail.what'),
      card: 'title',
      render: (m) => (
        <Stack gap={0}>
          <Group gap="xs">
            <Text size="sm" fw={500}>{m.documentType}</Text>
            {m.fromWave && (
              <Badge size="xs" color="blue" variant="light">{t('serialDetail.wave')}</Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">{m.tranCode} · {m.tranCodeName}</Text>
        </Stack>
      ),
    },
    {
      key: 'doc',
      label: t('serialDetail.doc'),
      render: (m) => (m.documentNumber
        ? <Code>{m.documentNumber}</Code>
        : <Text size="sm" c="dimmed">—</Text>),
    },
    {
      key: 'counterparty',
      label: t('serialDetail.counterparty'),
      render: (m) => (m.counterparty
        ? <Text size="sm">{m.counterparty}</Text>
        : <Text size="sm" c="dimmed">—</Text>),
    },
    {
      key: 'location',
      label: t('serialDetail.location'),
      card: 'aside',
      render: (m) => (
        <Stack gap={0}>
          <Code>{m.locationCode}</Code>
          {m.licensePlateNumber
            ? <Text size="xs" c="dimmed">{m.licensePlateNumber}</Text>
            : null}
        </Stack>
      ),
    },
    {
      key: 'delta',
      label: t('serialDetail.movement'),
      align: 'right',
      // In or out, said as a word. The number is always 1 or -1 for a serial, so the sign is
      // the only information in it and a bare "-1" makes a reader do the translation.
      render: (m) => (m.quantityDelta < 0
        ? <Badge size="sm" color="orange" variant="light">{t('serialDetail.out')}</Badge>
        : <Badge size="sm" color="teal" variant="light">{t('serialDetail.in')}</Badge>),
    },
  ], [t]);

  if (!warehouseId) {
    return (
      <Stack>
        <PageHeader title={t('serialDetail.title')} />
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          {t('serials.chooseWarehouse')}
        </Alert>
      </Stack>
    );
  }

  if (report.isLoading) {
    return (
      <Stack>
        <PageHeader title={t('serialDetail.title')} />
        <Center py="xl"><Loader color="invetrak" /></Center>
      </Stack>
    );
  }

  if (report.isError || !report.data) {
    return (
      <Stack>
        <PageHeader title={t('serialDetail.title')} />
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {report.error instanceof ApiError
            ? report.error.problem.title
            : t('serialDetail.errors.loadFailed')}
        </Alert>
      </Stack>
    );
  }

  const d = report.data.detail;
  const rows = history.data?.movements ?? [];
  const onHand = d.onHandQuantity > 0;

  return (
    <>
      <PageHeader
        title={t('serialDetail.titleFor', { serial: d.serialNumber })}
        actions={
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => router.history.back()}
          >
            {t('shell.back')}
          </Button>
        }
      />

      <Stack gap="lg">
        <Stack gap={4}>
          <Title order={4}>{d.itemNumber}</Title>
          {d.itemDescription ? <Text c="dimmed">{d.itemDescription}</Text> : null}
          {d.lotNumber ? (
            <Text size="sm" c="dimmed">
              {t('serialDetail.inLot', { lot: d.lotNumber })}
              {d.expirationDateUtc
                ? ` · ${t('serialDetail.expires', {
                  date: new Date(d.expirationDateUtc).toLocaleDateString(),
                })}`
                : ''}
            </Text>
          ) : null}
        </Stack>

        {/* Two facts, not four totals. A serial is one thing: it is here or it is not. */}
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          <Stat
            label={t('serialDetail.whereItIs')}
            value={onHand
              ? (d.currentLocationCode ?? t('serials.somewhereHere'))
              : t('serials.gone')}
            note={onHand && d.currentLicensePlateNumber
              ? t('serials.onPlate', { plate: d.currentLicensePlateNumber })
              : null}
          />
          <Stat
            label={t('serialDetail.lastSeen')}
            value={d.lastMovementAtUtc
              ? new Date(d.lastMovementAtUtc).toLocaleDateString()
              : '—'}
            note={d.firstSeenAtUtc
              ? t('serialDetail.firstSeen', {
                date: new Date(d.firstSeenAtUtc).toLocaleDateString(),
              })
              : null}
          />
        </SimpleGrid>

        <Stack gap="xs">
          <Title order={5}>{t('serialDetail.whoHasIt')}</Title>
          {report.data.customers.length === 0 ? (
            <Alert color="gray" variant="light">
              {onHand ? t('serialDetail.stillHere') : t('serialDetail.nobodyYet')}
            </Alert>
          ) : (
            <ResponsiveTable
              rows={report.data.customers}
              columns={customerColumns}
              rowKey={(c) => `${c.salesOrderId}-${c.shippedAtUtc ?? ''}`}
              minWidth={640}
              striped
            />
          )}
        </Stack>

        <Stack gap="xs">
          <Title order={5}>{t('serialDetail.everything')}</Title>
          {history.isLoading && <Center py="md"><Loader size="sm" color="invetrak" /></Center>}
          {history.isSuccess && rows.length === 0 && (
            <Alert color="gray" variant="light">{t('serialDetail.noMovements')}</Alert>
          )}
          {rows.length > 0 && (
            <>
              <ResponsiveTable
                rows={rows}
                columns={movementColumns}
                rowKey={(m) => m.inventoryTransactionId}
                minWidth={820}
                striped
              />
              {history.data!.totalCount > rows.length && (
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">
                    {t('serialDetail.showing', {
                      shown: rows.length,
                      total: history.data!.totalCount,
                    })}
                  </Text>
                  <Group gap="xs">
                    <Button size="xs" variant="default" disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      {t('serialDetail.previous')}
                    </Button>
                    <Button size="xs" variant="default"
                      disabled={page * HISTORY_PAGE >= history.data!.totalCount}
                      onClick={() => setPage((p) => p + 1)}>
                      {t('serialDetail.next')}
                    </Button>
                  </Group>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </>
  );
}

/**
 * One fact about the unit.
 *
 * Labelled as a group so the value can be read back WITH its label: "A-01" on its own does not
 * say whether that is where the unit is or where it was last counted.
 */
function Stat({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <Card withBorder padding="sm" radius="md" role="group" aria-label={label}>
      <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
      <Text size="xl" fw={600}>{value}</Text>
      {note ? <Text size="xs" c="dimmed">{note}</Text> : null}
    </Card>
  );
}
